import { useAuth } from 'contexts/auth';
import { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const webSocketContext = createContext();
const useWebsocket = () => useContext(webSocketContext);

const initialMessageList = {
    normalMessage: null,
    systemMessage: null,
};

const messageReducer = (state, action) => {
    const { type, payload } = action;

    switch (type) {
        case 'NORMAL_MESSAGE':
            if (JSON.stringify(state?.normalMessage) === JSON.stringify(payload))
                return { ...state, normalMessage: null };

            return {
                ...state,
                normalMessage: payload,
            };
        case 'DELETE_MESSAGE':
            if (JSON.stringify(state?.normalMessage) === JSON.stringify(payload))
                return { ...state, normalMessage: null };

            return {
                ...state,
                normalMessage: payload,
            };
        case 'SYSTEM_MESSAGE': {
            if (JSON.stringify(state?.systemMessage) === JSON.stringify(payload))
                return {
                    ...state,
                    systemMessage: payload,
                };
            return {
                ...state,
                systemMessage: payload,
            };
        }
        default: {
            throw new Error('Unknown subtask action type');
        }
    }
};

const WebsocketProvider = (props) => {
    const { auth, sessionExpired } = useAuth();
    const location = useLocation();
    const [messageWS, dispatchMessageAction] = useReducer(messageReducer, initialMessageList);
    const [batchWS, setBatchWS] = useState();
    const [taskWS, setTaskWS] = useState();
    const [teamWS, setTeamWS] = useState();
    const [reloadModal, setReloadModal] = useState(false);
    const [isWsConnected, setIsWsConnected] = useState(false);
    const ws = useRef(null);

    const callOnMessage = useCallback(
        (msg) => {
            const { data = {} } = msg;

            switch (data.payload_type) {
                case 'session_expired':
                    return sessionExpired();
                case 'updated_teams':
                    sendOn('default', { payload_type: 'ping' });
                    return setTeamWS(data);
                case 'send':
                    return dispatchMessageAction({ type: 'NORMAL_MESSAGE', payload: msg });
                case 'delete':
                    return dispatchMessageAction({ type: 'DELETE_MESSAGE', payload: msg });
                case 'add_channel':
                case 'create_task':
                case 'upsert_task':
                case 'update_task':
                case 'delete_task':
                case 'move_task':
                case 'create_task_resource':
                case 'delete_task_resource':
                case 'create_subtask':
                case 'update_subtask':
                case 'delete_subtask':
                case 'task_error':
                case 'update_template':
                case 'template_error':
                case 'create_ai_feedback':
                case 'update_ai_feedback':
                    return setTaskWS(data);
                case 'pong':
                    return setBatchWS(data?.payload?.batch_status);
                default:
                    return;
            }
        },
        [sessionExpired]
    );

    const ping = useCallback(() => {
        if (!isWsConnected) return;
        const data = { payload_type: 'ping' };
        if (location.pathname.startsWith('/assignments')) {
            const assignmentId = location.pathname.split('/')[2];
            if (location.pathname.split('/').length < 5 && assignmentId) {
                data.payload = { assignment_id: assignmentId };
            }
        }
        sendOn('default', data);
    }, [isWsConnected, location.pathname]);

    const connectWebsocket = useCallback(async () => {
        if ('WebSocket' in window) {
            console.log('Connecting WS...');

            ws.current = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL);

            ws.current.onopen = () => {
                sendOn('default', { payload_type: 'connect' });
            };

            ws.current.onmessage = (msg) => callOnMessage(JSON.parse(msg.data));

            ws.current.onerror = () => {
                setReloadModal(true);
            };

            ws.current.onclose = () => {
                console.log('WS disconnected', new Date());
                setTimeout(connectWebsocket, 5000);
            };

            setIsWsConnected(true);
        } else {
            console.log('WebSocket is not supported by the browser.');
        }
    }, [callOnMessage]);

    const sendCustomWSOn = async (route, data) => {
        if (ws.current.readyState === 1) {
            return ws.current.send(
                JSON.stringify({
                    route,
                    data: {
                        ...data,
                        auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
                    },
                })
            );
        } else {
            setReloadModal(true);
        }
    };

    const sendOn = async (action, data) => {
        if (ws.current.readyState === 1) {
            return ws.current.send(
                JSON.stringify({
                    action,
                    data: {
                        ...data,
                        auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
                    },
                })
            );
        } else {
            setReloadModal(true);
        }
    };

    const disconnectWebsocket = () => {
        if (!ws || !ws.current) return;
        ws.current.close();
    };

    const getWSStatus = () => {
        if (!ws || ws.current.readyState !== 1) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (auth?.owner_account_id) {
            connectWebsocket();
        } else {
            disconnectWebsocket();
        }

        return () => {
            disconnectWebsocket();
        };
    }, [auth?.owner_account_id, connectWebsocket]);

    useEffect(() => {
        const id = setInterval(() => {
            if (isWsConnected) ping();
        }, 15000);

        return () => {
            clearInterval(id);
        };
    }, [ping, isWsConnected]);

    return (
        <webSocketContext.Provider
            {...props}
            value={{
                connectWebsocket,
                disconnectWebsocket,
                callOnMessage,
                sendOn,
                sendCustomWSOn,
                getWSStatus,
                ws,
                messageWS,
                reloadModal,
                batchWS,
                setBatchWS,
                taskWS,
                setTaskWS,
                teamWS,
                setTeamWS,
            }}
        />
    );
};

export { useWebsocket };
export default WebsocketProvider;
